import Lookup from '@/components/UI/Lookup';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { Endpoints } from '@/generated/internal-api/Endpoints';
import type { components } from '@/generated/internal-api/openapi-types';
import type { ClassificationType, TreatmentType } from '@/model/Treatment.model';
import { ApiService } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { type FC, useState } from 'react';
import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils.tsx';

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1.schema'];

export interface TreatmentLookupProps {
  value?: TreatmentType;
  onChange: (treatmentType?: TreatmentType) => void;
  onBlur: () => void;
  isDisabled?: boolean;
  classificationType: ClassificationType;
}

export interface OptionType {
  label: string;
  value: TreatmentType | undefined;
}

/**
 * The glossary list endpoint describes a treatment type without its Benchling registry attributes,
 * so they are filled in as absent rather than left undefined — the treatment create payload requires
 * `entity_id` and `benchling_link` to be present, and `Lookup` reads them to decide whether to render
 * the registry badge.
 */
const toTreatmentType = (treatment: TreatmentTypeGlossary): TreatmentType => ({
  id: treatment.id,
  name: treatment.name,
  created_at: treatment.created_at,
  updated_at: treatment.updated_at,
  benchling_link: null,
  entity_id: null,
  registry_id: null,
  in_use: null,
});

export const TreatmentLookup: FC<TreatmentLookupProps> = ({
  value,
  onChange,
  onBlur,
  isDisabled = false,
  classificationType,
}) => {
  const isMounted = useMountedState();
  const { newAbortController } = useAbortController();
  const [isLoading, setLoading] = useState(true);
  const [isComplete, setComplete] = useState(false);

  const { invoke: getTreatmentTypes } = useApiHook({
    endpoint: 'GET /api/v1/treatment-types',
    invokeOnInit: false,
  });

  const loadTokenOptions = async ({ inputValue, token }: { inputValue: string; token: string }) => {
    try {
      setLoading(true);
      setComplete(false);

      const query: Record<string, string> = {};

      if (_isNotEmpty(token)) {
        query.nextToken = token;
      }

      const trimmedQuery = (inputValue ?? '').trim();
      if (_isNotEmpty(trimmedQuery)) {
        query.searchBy = trimmedQuery;
      }

      const response = await getTreatmentTypes({ query });

      if (response.type === 'success') {
        const treatments = response.body.treatments ?? [];
        const nextToken = response.body.nextToken;

        const options: Array<OptionType> = treatments.map(
          (treatment) =>
            ({
              label: treatment.name,
              value: treatment,
            }) as OptionType
        );

        if (isComplete) {
          setLoading(false);
        }
        return {
          options,
          nextToken,
        };
      }
    } catch (error) {
      setLoading(false);
    }
    return { options: [], nextToken: null };
  };

  const loadOptions = async ({ inputValue, page }: { inputValue: string; page: string | number }) => {
    try {
      setLoading(true);

      const query: Endpoints['GET /api/v1/team-glossary/treatment-types']['query'] = {
        page: Number(page),
        'query_by[]': 'name',
        'filters[]': ['status|eq|active'],
      };

      const trimmedQuery = (inputValue ?? '').trim();
      if (_isNotEmpty(trimmedQuery)) {
        query.query = trimmedQuery;
      }

      const response = await ApiService.call({
        endpoint: 'GET /api/v1/team-glossary/treatment-types',
        path: undefined,
        query,
        signal: newAbortController().signal,
        options: { onError: { toast: false, slug: 'treatment-lookup-glossary' } },
      });

      if (isMounted()) {
        setLoading(false);
      }

      if (response.type === 'success') {
        const { data, links } = response.body;
        const options: Array<OptionType> = data.map((treatment) => ({
          label: treatment.name,
          value: toTreatmentType(treatment),
        }));

        if (_notNil(links.next)) {
          return {
            options,
            hasMore: true,
            additional: { page: new URL(links.next).searchParams.get('page') ?? '' },
          };
        }

        setLoading(false);
        setComplete(true);
        return { options, hasMore: false, additional: { page } };
      }
    } catch (error) {
      if (isMounted()) {
        setLoading(false);
      }
    }
    return { options: [], hasMore: false, additional: { page } };
  };

  return (
    <Lookup
      name={classificationTypes[classificationType].key}
      defaultValue={_isNil(value) ? null : { label: value.name, value }}
      loadOptions={loadOptions}
      loadTokenOptions={loadTokenOptions}
      tokenPagination={true}
      defaultPagination={true}
      handleSelect={(option: OptionType) => {
        return onChange?.(option?.value);
      }}
      onBlur={onBlur}
      disabled={isDisabled}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      additional={{ page: 1, perPage: 10 }}
      isLoading={isLoading}
      placeholder={`Select ${classificationTypes[classificationType].key}...`}
      noOptionsMessage={({ inputValue = '' }) =>
        !isComplete || isLoading ? 'Loading...' : _isEmpty(inputValue) ? 'No Entries' : `No Entries match ${inputValue}`
      }
    />
  );
};
